(() => {
  const SELECTOR = '.zj-private-events';

  const init = (root) => {
    const track = root.querySelector('[data-gallery-track]');
    if (!track || track.dataset.ready === 'true') return;
    track.dataset.ready = 'true';

    const cards = [...track.querySelectorAll('.zj-private-events__gallery-card')];
    const previous = root.querySelector('[data-gallery-prev]');
    const next = root.querySelector('[data-gallery-next]');
    const dots = root.querySelector('[data-gallery-dots]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let animationFrame;
    let dragging = false;
    let dragStartX = 0;
    let dragStartScroll = 0;

    const step = () => {
      const card = cards[0];
      if (!card) return 0;
      const styles = getComputedStyle(track);
      return card.getBoundingClientRect().width + (parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0);
    };

    const lastIndex = () => {
      const distance = step();
      if (!distance) return 0;
      return Math.max(0, Math.ceil((track.scrollWidth - track.clientWidth - 1) / distance));
    };

    const currentIndex = () => {
      const distance = step();
      if (!distance) return 0;
      return Math.min(lastIndex(), Math.max(0, Math.round(track.scrollLeft / distance)));
    };

    const paint = () => {
      animationFrame = undefined;
      const index = currentIndex();
      const end = lastIndex();
      const canScroll = end > 0;

      if (previous) {
        previous.hidden = !canScroll;
        previous.disabled = !canScroll || index === 0;
      }
      if (next) {
        next.hidden = !canScroll;
        next.disabled = !canScroll || index === end;
      }
      if (dots) {
        dots.hidden = !canScroll;
        [...dots.children].forEach((dot, dotIndex) => dot.toggleAttribute('aria-current', dotIndex === index));
      }
    };

    const schedulePaint = () => {
      if (animationFrame) return;
      animationFrame = requestAnimationFrame(paint);
    };

    const goTo = (index, instant = false) => {
      const distance = step();
      if (!distance) return;
      const destination = Math.min(lastIndex(), Math.max(0, index));
      track.scrollTo({
        left: destination * distance,
        behavior: instant || reducedMotion.matches ? 'auto' : 'smooth'
      });
      schedulePaint();
    };

    previous?.addEventListener('click', () => goTo(currentIndex() - 1));
    next?.addEventListener('click', () => goTo(currentIndex() + 1));

    dots?.addEventListener('click', (event) => {
      const index = [...dots.children].indexOf(event.target);
      if (index >= 0) goTo(index);
    });

    track.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') goTo(0);
      else if (event.key === 'End') goTo(lastIndex());
      else goTo(currentIndex() + (event.key === 'ArrowRight' ? 1 : -1));
    });

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || lastIndex() === 0) return;
      dragging = true;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      track.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
    });

    const finishDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      goTo(currentIndex());
    };

    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
    track.addEventListener('scroll', schedulePaint, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      goTo(currentIndex(), true);
      paint();
    });
    resizeObserver.observe(track);

    const onUnload = (event) => {
      if (!event.target.contains(root)) return;
      resizeObserver.disconnect();
      if (animationFrame) cancelAnimationFrame(animationFrame);
      document.removeEventListener('shopify:section:unload', onUnload);
    };
    document.addEventListener('shopify:section:unload', onUnload);

    paint();
  };

  const initAll = (scope = document) => scope.querySelectorAll(SELECTOR).forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
